<template>
    <div>
        <form v-if="!calculated" @submit.prevent="submit">
            <div class="form-group">
                <label>Enter Amount:</label>
                <input
                    type="text"
                    v-model="form.amount"
                    class="form-control"
                    placeholder="Enter amount"
                    :class="{ 'is-invalid': errors.amount }"
                />
                <div v-if="errors.amount" class="invalid-feedback">
                    {{ errors.amount }}
                </div>
            </div>

            <div class="form-group">
                <label>From (Currency):</label>
                <div class="currency-selector">
                    <img
                        :src="getFlagUrl(form.fromCurrency)"
                        alt="Flag"
                        class="currency-flag"
                    />
                    <select v-model="form.fromCurrency" class="form-control">
                        <option
                            v-for="currency in currencies"
                            :key="currency"
                            :value="currency"
                        >
                            {{ currency }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="form-group">
                <label>To (Currency):</label>
                <div class="currency-selector">
                    <img
                        :src="getFlagUrl(form.toCurrency)"
                        alt="Flag"
                        class="currency-flag"
                    />
                    <select v-model="form.toCurrency" class="form-control">
                        <option
                            v-for="currency in currencies"
                            :key="currency"
                            :value="currency"
                        >
                            {{ currency }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="form-group">
                <button
                    class="btn btn-primary btn-block"
                    :disabled="form.loading"
                >
                    Convert
                </button>
            </div>
        </form>
        <div v-if="calculated" class="result-container slide-in mt-4">
            <div class="alert alert-info text-center">
                <!-- Highlighted Converted Amount -->
                <p class="result-converted">
                    {{ form.amount }} {{ form.fromCurrency }} is approximately
                    <b>{{ result.converted }}</b> {{ form.toCurrency }}.
                </p>

                <!-- Muted Exchange Rates -->
                <p class="exchange-rate muted-text">
                    1.00 {{ form.fromCurrency }} = {{ rate.toFixed(4) }} {{ form.toCurrency }}
                </p>
                <p class="exchange-rate muted-text">
                    1.00 {{ form.toCurrency }} = {{ (1 / rate).toFixed(4) }} {{ form.fromCurrency }}
                </p>
            </div>
            <button @click="reset" class="btn btn-secondary btn-block">
                Perform Another Calculation
            </button>
        </div>
    </div>
</template>

<script>
import axios from "axios";

export default {
    data() {
        return {
            form: {
                amount: "",
                fromCurrency: "ZWG",
                toCurrency: "USD",
                loading: false,
            },
            currencies: [], // List of available currencies
            calculated: false,
            result: { converted: 0 },
            rate: null,
            zwgRate: null, // ZWG rate storage
            errors: {
                amount: "",
            },
        };
    },
    mounted() {
        this.fetchCurrencies();
        this.fetchZWGRate();
    },
    methods: {
        async fetchCurrencies() {
            try {
                const response = await axios.get(
                    "https://exchange-rate-api1.p.rapidapi.com/latest?base=USD",
                    {
                        headers: {
                            "x-rapidapi-key":
                                "0fb1038921msh56a5258f3af81e5p105a9ajsn546a783d112c",
                            "x-rapidapi-host":
                                "exchange-rate-api1.p.rapidapi.com",
                        },
                    }
                );

                this.currencies = Object.keys(response.data.rates);

                if (!this.currencies.includes("ZWG")) {
                    this.currencies.push("ZWG");
                }
            } catch (error) {
                console.error("Error fetching currencies:", error);
            }
        },

        async fetchZWGRate() {
            try {
                const response = await axios.get("/zwg-rate");
                this.zwgRate = response.data.rate;
            } catch (error) {
                console.error("Error fetching ZWG rate:", error);
            }
        },

        validateForm() {
            this.errors.amount = "";

            if (!this.form.amount || isNaN(this.form.amount)) {
                this.errors.amount = "Please enter a valid amount.";
                return false;
            }

            return true;
        },

        async submit() {
            if (!this.validateForm()) {
                return;
            }

            this.form.loading = true;

            try {
                let toRate;

                if (
                    this.form.fromCurrency === "ZWG" ||
                    this.form.toCurrency === "ZWG"
                ) {
                    if (!this.zwgRate) {
                        this.errors.amount = "ZWG rate not available.";
                        return;
                    }

                    if (this.form.fromCurrency === "ZWG") {
                        toRate = 1 / this.zwgRate; // Convert from ZWG to base currency (e.g., USD)
                    } else {
                        toRate = this.zwgRate; // Convert from base currency (e.g., USD) to ZWG
                    }
                } else {
                    const response = await axios.get(
                        `https://exchange-rate-api1.p.rapidapi.com/latest?base=${this.form.fromCurrency}`,
                        {
                            headers: {
                                "x-rapidapi-key":
                                    "0fb1038921msh56a5258f3af81e5p105a9ajsn546a783d112c",
                                "x-rapidapi-host":
                                    "exchange-rate-api1.p.rapidapi.com",
                            },
                        }
                    );

                    const rates = response.data.rates;
                    toRate = rates[this.form.toCurrency];

                    if (!toRate) {
                        this.errors.amount = "Invalid currency selected.";
                        return;
                    }
                }

                this.rate = toRate;
                this.result.converted = (this.form.amount * toRate).toFixed(2);
                this.calculated = true;
            } catch (error) {
                console.log("Error during conversion:", error);
            } finally {
                this.form.loading = false;
            }
        },

        getFlagUrl(currency) {
            return `https://flagcdn.com/w40/${currency
                .substring(0, 2)
                .toLowerCase()}.png`;
        },

        reset() {
            this.form.amount = "";
            this.calculated = false;
            this.errors.amount = "";
        },
    },
};
</script>

<style>

.result-converted {
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
}


.exchange-rate {
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 0.25rem;
}
.currency-flag {
    width: 20px;
    height: 15px;
    margin-right: 8px;
    vertical-align: middle;
}

.currency-selector {
    display: flex;
    align-items: center;
}

.currency-selector select {
    margin-left: 8px;
    flex-grow: 1;
}

.slide-in {
    animation: slideIn 0.5s ease-in-out;
}

@keyframes slideIn {
    from {
        transform: translateX(-100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}
</style>
